// DOM + archive glue for the front-end (szzt-spec.md §14 ingest/preview). THIN and typecheck-
// ONLY: like `@szzt/gateway`'s `sw.ts`, `@szzt/verify`'s `reader.ts`, and `@szzt/publish`'s
// `client.ts`, this module is exercised by `tsc` alone — never in the unit tests — because it touches
// live browser globals (DataTransfer, FileSystemEntry, DecompressionStream, HTMLIFrameElement). All
// load-bearing decisions live in the pure modules it calls: the §3.1/§A.4 ceiling + decompression-
// bomb guards (`ingest.ts`) and the verifying preview engine (`preview.ts`, which is the gateway's).
//
// UNVERIFIED-vs-real caveat (carried forward): this adapter is not run against a real browser here.

import type { FileInput } from "@szzt/core";
import {
  assertArchiveWithinCeilings,
  RatioGuard,
  type ArchiveEntryMeta,
} from "./ingest.ts";
import { createPreview, renderPreviewFrame, type Preview } from "./preview.ts";
import type { ServeMode } from "@szzt/gateway";

// --- File ingestion (§14 "Ingest"): drag-and-drop, directory picker, loose files --------------------

/** Read a `File`'s exact bytes. */
export async function readFileBytes(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.arrayBuffer());
}

/**
 * Collect `FileInput[]` from a drag-and-drop `DataTransfer`, recursing directory entries via the
 * (non-standard but universal) `webkitGetAsEntry` API. Paths are relative and `/`-separated; the
 * normalization, junk-stripping, and root inference happen later in `@szzt/core`'s `build` (§14).
 */
export async function filesFromDataTransfer(dt: DataTransfer): Promise<FileInput[]> {
  const roots: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items)) {
    if (item.kind !== "file") continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) roots.push(entry);
  }
  if (roots.length === 0) {
    // No entry API: fall back to the flat file list (loses subdirectory structure).
    return Promise.all(Array.from(dt.files).map(async (f) => fileInput(f.name, f)));
  }
  const out: FileInput[] = [];
  for (const entry of roots) await walkEntry(entry, out);
  return out;
}

/** Collect `FileInput[]` from a `<input type="file" webkitdirectory>` selection. */
export async function filesFromFileList(files: FileList | File[]): Promise<FileInput[]> {
  return Promise.all(
    Array.from(files).map(async (f) => fileInput(f.webkitRelativePath || f.name, f)),
  );
}

async function fileInput(path: string, file: File): Promise<FileInput> {
  return { path, bytes: await readFileBytes(file) };
}

async function walkEntry(entry: FileSystemEntry, out: FileInput[]): Promise<void> {
  if (entry.isFile) {
    const file = await getFile(entry as FileSystemFileEntry);
    out.push({ path: entry.fullPath.replace(/^\//, ""), bytes: await readFileBytes(file) });
  } else if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    for (const child of await readAllEntries(reader)) await walkEntry(child, out);
  }
}

function getFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/** `readEntries` returns at most ~100 entries per call, so drain it until empty. */
async function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  const all: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) return all;
    all.push(...batch);
  }
}

// --- Zip ingestion (§14) with decompression-bomb control (§3.1, §A.4 #11) ---------------------------

interface ZipEntry extends ArchiveEntryMeta {
  method: number; // 0 = stored, 8 = deflate
  localHeaderOffset: number;
}

/**
 * Unpack a zip to `FileInput[]`, enforcing the §3.1 ceilings against DECLARED decompressed sizes and
 * the §A.4 ratio bound BEFORE inflating any entry, then streaming each inflate through a
 * {@link RatioGuard} backstop so a lying archive is aborted mid-stream (§14). Directory entries are
 * skipped; the resulting relative paths flow into `@szzt/core`'s normalization.
 */
export async function unzipToFileInputs(zipBytes: Uint8Array): Promise<FileInput[]> {
  const entries = parseZipCentralDirectory(zipBytes);
  assertArchiveWithinCeilings(entries); // fail fast on declared sizes (§3.1, §A.4)

  const guard = new RatioGuard();
  const out: FileInput[] = [];
  for (const e of entries) {
    if (e.path.endsWith("/") && e.uncompressedSize === 0) continue; // directory marker
    guard.nextEntry();
    const compressed = sliceLocalData(zipBytes, e);
    guard.addInput(compressed.byteLength);
    const bytes = e.method === 0 ? compressed : await inflateRaw(compressed, guard);
    if (e.method === 0) guard.addOutput(bytes.byteLength);
    out.push({ path: e.path, bytes });
  }
  return out;
}

const EOCD_SIG = 0x06054b50;
const CDH_SIG = 0x02014b50;

function parseZipCentralDirectory(buf: Uint8Array): ZipEntry[] {
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  // Locate the End Of Central Directory record by scanning backwards for its signature.
  let eocd = -1;
  for (let i = buf.byteLength - 22; i >= 0; i--) {
    if (dv.getUint32(i, true) === EOCD_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("zip: End Of Central Directory not found");
  const count = dv.getUint16(eocd + 10, true);
  let ptr = dv.getUint32(eocd + 16, true);

  const entries: ZipEntry[] = [];
  const dec = new TextDecoder();
  for (let n = 0; n < count; n++) {
    if (dv.getUint32(ptr, true) !== CDH_SIG) throw new Error("zip: bad central directory header");
    const method = dv.getUint16(ptr + 10, true);
    const compressedSize = dv.getUint32(ptr + 20, true);
    const uncompressedSize = dv.getUint32(ptr + 24, true);
    const nameLen = dv.getUint16(ptr + 28, true);
    const extraLen = dv.getUint16(ptr + 30, true);
    const commentLen = dv.getUint16(ptr + 32, true);
    const localHeaderOffset = dv.getUint32(ptr + 42, true);
    const path = dec.decode(buf.subarray(ptr + 46, ptr + 46 + nameLen));
    entries.push({ path, method, compressedSize, uncompressedSize, localHeaderOffset });
    ptr += 46 + nameLen + extraLen + commentLen;
  }
  return entries;
}

/** Slice an entry's compressed payload, resolving the variable-length local header. */
function sliceLocalData(buf: Uint8Array, e: ZipEntry): Uint8Array {
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const o = e.localHeaderOffset;
  const nameLen = dv.getUint16(o + 26, true);
  const extraLen = dv.getUint16(o + 28, true);
  const start = o + 30 + nameLen + extraLen;
  return buf.subarray(start, start + e.compressedSize);
}

/** Inflate a raw-deflate stream via the browser `DecompressionStream`, metering output through the guard. */
async function inflateRaw(compressed: Uint8Array, guard: RatioGuard): Promise<Uint8Array> {
  const ds = new DecompressionStream("deflate-raw");
  const writer = ds.writable.getWriter();
  // lib.dom's `BufferSource` wants `ArrayBufferView<ArrayBuffer>`; our bytes are
  // `Uint8Array<ArrayBufferLike>` (the SharedArrayBuffer union). Narrow cast — same as gateway sw.ts.
  void writer.write(compressed as unknown as BufferSource);
  void writer.close();
  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    guard.addOutput(value.byteLength); // aborts mid-stream on a bomb
    chunks.push(value);
    total += value.byteLength;
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.byteLength;
  }
  return out;
}

// --- Sandboxed preview mount (§14 "Preview") --------------------------------------------------------

/**
 * Serve a preview path through the verifying engine and mount the result in a SANDBOXED iframe,
 * isolating just-built content from the publish UI's origin (§14). The iframe is given `srcdoc` so it
 * has an opaque origin and no access to the parent; only HTML is mounted this way (sub-resources are
 * served on demand through the same preview by the app's own fetch interception).
 */
export async function mountPreview(
  iframe: HTMLIFrameElement,
  preview: Preview,
  path = "/",
  mode: ServeMode = "site",
): Promise<void> {
  // Load-bearing sandboxing lives in the unit-tested `renderPreviewFrame`; this is DOM glue only.
  const frame = await renderPreviewFrame(preview, path, mode);
  iframe.setAttribute("sandbox", frame.sandbox);
  iframe.srcdoc = frame.srcdoc;
}

export { createPreview };
